import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button } from '@wot/components';
import { INVOICES, MANAGEMENT, PAYMENTS, PLANS } from '../../assets/icons';
import TextInput from '../../components/TextInput/TextInput';
import Select from '../../components/Select/Select';
import Footer from '../../components/Landing/Footer';
import ticketsApi from '../../api/tickets';
import { SEO } from '../../components/SEO';

const topicOptions = [
  { value: 'management', label: 'Tutor & classes Management' },
  { value: 'payments', label: 'Payments' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'plans', label: 'Plans' }
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

interface TicketFormData {
  topic: string;
  fullName: string;
  subject: string;
  description: string;
  priority: string;
  email: string;
  phoneNumber: string;
}

export default function RaiseTicket() {
  const [showForm, setShowForm] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const { register, handleSubmit, reset, formState: { errors } } = useForm<TicketFormData>();
  const [loading, setLoading] = useState(false);

  // Sample data for pre-filling (for testing)
  const sampleData = {
    fullName: "John Doe",
    description: "hasdjkghjk",
    email: "asdsa@asdas.com",
    phoneNumber: "12355555",
    priority: "high",
    subject: "Math",
    topic: "payments"
  };

  const onSubmit: SubmitHandler<TicketFormData> = async (data) => {
    setLoading(true);
    
    try {
      const response = await ticketsApi.createTicket({
        ...data,
        topic: selectedTopic
      });
      
      if (response.status === 'OK' || response.status === 'CREATED') {
        toast.success('Ticket submitted successfully!');
        reset();
        setShowForm(false);
        setSelectedTopic('');
      } else {
        toast.error('Failed to submit ticket. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Error submitting ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

const handleTopicSelect = (topic: string) => {
    setSelectedTopic(topic);
    setShowForm(true);
    console.log('Topic selected:', topic);
  };

  const handlePreFillSampleData = () => {
    setSelectedTopic(sampleData.topic);
    setShowForm(true);
    // Reset form with sample data after a short delay to ensure form is rendered
    setTimeout(() => {
      reset(sampleData);
    }, 100);
    console.log('Pre-filled with sample data:', sampleData);
  };

  if (showForm) {
    return (
      <>
      <SEO 
        title="Raise a Ticket - Submit Support Request"
        description="Submit a support ticket for help with your account, payments, invoices, or tutoring plans."
        keywords="support ticket, help, customer service, raise ticket"
        url="/raise-ticket"
        type="website"
        noIndex={true}
      />
      <div className="bg-[#F0F3F5] py-8">
        <div className="bg-white w-11/12 m-auto rounded-lg">
          <div className="flex flex-col p-10 gap-5">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
              <h2 className="text-3xl font-medium">Raise a Ticket</h2>
            </div>
            
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Selected Topic: <strong>{topicOptions.find(t => t.value === selectedTopic)?.label}</strong></p>
              </div>
              
              <input 
                type="hidden" 
                {...register('topic', { required: true })} 
                value={selectedTopic}
              />
              
              <TextInput
                label="Full Name"
                name="fullName"
                register={register}
                errors={errors}
                required
                placeholder="Enter your full name"
              />
              
              <TextInput
                label="Subject"
                name="subject"
                register={register}
                errors={errors}
                required
                placeholder="Brief description of your issue"
              />
              
              <div className="w-full">
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('description', { required: 'Description is required' })}
                  rows={4}
                  placeholder="Please provide detailed information about your issue..."
                  className="block w-full text-base appearance-none focus:outline-none bg-transparent pl-4 pr-4 py-4 border border-blue-300 rounded-md placeholder-gray-500 resize-none"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>
              
              <Select
                label="Priority"
                name="priority"
                register={register}
                options={priorityOptions}
                required
              />
              
              <TextInput
                label="Email"
                name="email"
                type="email"
                register={register}
                errors={errors}
                required
                placeholder="your.email@example.com"
                pattern={{
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }}
              />
              
              <TextInput
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                register={register}
                errors={errors}
                placeholder="+1 (555) 123-4567"
              />
              
              <div className="flex gap-4">
                <Button
                  text="Cancel"
                  onClick={() => setShowForm(false)}
                  type="secondary"
                  block
                />
                <Button
                  text="Submit Ticket"
                  onClick={handleSubmit(onSubmit)}
                  type="primary"
                  loading={loading}
                  block
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <SEO 
      title="Raise a Ticket - Support Center"
      description="Submit a support ticket for help with your account, payments, invoices, or tutoring plans."
      keywords="support ticket, help, customer service, raise ticket"
      url="/raise-ticket"
      type="website"
      noIndex={true}
    />
    <div>
      <div className="bg-[#F0F3F5] py-8">
        <div className="bg-white w-11/12 m-auto rounded-lg">
          <div className="flex flex-col p-10 gap-5">
            <div className="flex flex-col gap-16">
              <span className="text-4xl font-medium">Report an Issue</span>
              <span className="text-2xl font-medium">Select topic</span>
            </div>
            <div className="flex justify-between">
              <div 
                className="flex flex-col items-center border-2 border-neutralGray3 rounded-lg px-12 py-12 gap-4 cursor-pointer basis-1/5 hover:border-blue-500 transition-colors"
                onClick={() => handleTopicSelect('management')}
              >
                <img src={MANAGEMENT} alt="Topic-Management Icon" />
                <span className="text-xl font-normal text-center">Tutor & classes Management</span>
              </div>
              <div 
                className="flex flex-col items-center border-2 border-neutralGray3 rounded-lg px-12 py-12 gap-4 cursor-pointer basis-1/5 hover:border-blue-500 transition-colors"
                onClick={() => handleTopicSelect('payments')}
              >
                <img src={PAYMENTS} alt="Topic-payments Icon" />
                <span className="text-xl font-normal text-center">Payments</span>
              </div>
              <div 
                className="flex flex-col items-center border-2 border-neutralGray3 rounded-lg px-12 py-12 gap-4 cursor-pointer basis-1/5 hover:border-blue-500 transition-colors"
                onClick={() => handleTopicSelect('invoices')}
              >
                <img src={INVOICES} alt="Topic-invoices Icon" />
                <span className="text-xl font-normal text-center">Invoices</span>
              </div>
              <div 
                className="flex flex-col items-center border-2 border-neutralGray3 rounded-lg px-12 py-12 gap-4 cursor-pointer basis-1/5 hover:border-blue-500 transition-colors"
                onClick={() => handleTopicSelect('plans')}
              >
                <img src={PLANS} alt="Topic-plans Icon" />
                <span className="text-xl font-normal text-center">Plans</span>
              </div>
            </div>

            <div className="bg-neutralBlue2 flex flex-row justify-between items-center rounded-lg p-5">
              <div className="flex flex-col gap-2">
                <span className="text-2xl font-medium">Still need help?</span>
                <span className="text-base font-normal">We are here for you</span>
              </div>
            
            </div>
          </div>
        </div>
      </div>
      <div className="bg-neutralBlack">
          <Footer />
        </div>
    </div>
    </>
  );
}
